import React, { useEffect, useRef, useState } from "react";
import FontAwesome from "react-fontawesome";

/**
 * Re-usable icon button that opens a small menu with row-level actions.
 * @param {{
 *   label: string,
 *   items: { label: string, icon?: string, onSelect: () => void }[],
 *   icon?: string,
 *   variant?: "default" | "inline"
 * }} props
 */
export function RowActionsMenu({
	label,
	items,
	icon = "ellipsis-h",
	variant = "default",
}) {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const handleClickOutside = (event) => {
			if (!containerRef.current) return;
			if (containerRef.current.contains(event.target)) return;
			setIsOpen(false);
			if (typeof event.preventDefault === "function") {
				event.preventDefault();
			}
			if (typeof event.stopPropagation === "function") {
				event.stopPropagation();
			}
		};

		document.addEventListener("mousedown", handleClickOutside, true);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside, true);
		};
	}, [isOpen]);

	if (!items || items.length === 0) {
		return null;
	}

	const handleSelect = (item) => {
		setIsOpen(false);
		item.onSelect();
	};

	const className = [
		"row-actions-menu",
		`row-actions-menu--${variant}`,
		isOpen ? "row-actions-menu--open" : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div
			className={`row-actions-menu row-actions-menu--${variant}`}
			ref={containerRef}
		>
			<button
				type="button"
				className="row-actions-menu__trigger"
				onClick={() => setIsOpen((open) => !open)}
				aria-label={label}
				aria-expanded={isOpen}
				aria-haspopup="menu"
			>
				<FontAwesome name={icon} />
			</button>
			{isOpen && (
				<div className="row-actions-menu__list" role="menu">
					{items.map((item) => (
						<button
							type="button"
							key={item.label}
							className="row-actions-menu__item"
							role="menuitem"
							onClick={() => handleSelect(item)}
						>
							{item.icon ? (
								<FontAwesome
									name={item.icon}
									className="row-actions-menu__icon"
								/>
							) : null}
							<span>{item.label}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
